// frontend/src/modules/esign/pages/TemplateEditorPage.tsx
//
// Single-page template editor (rename, signing order, roles, field
// placements) — unlike PrepareWizardPage's multi-step upload flow, there is
// no "new template from scratch" step here: templates are only ever born
// via "Save as template" on an existing document (`esignTemplatesApi.
// fromDocument`, called from DocumentDetailPage), because the backend has
// no template-upload route (`saveTemplate` requires `originalBlobUrl` to
// create — see templates.service.ts's `missing_original_blob_url` guard).
// This page only ever PUTs (full-replace) an existing template.
//
// Roles reuse RecipientsEditor's `DraftRecipient`/`makeLocalId` plumbing and
// FieldEditor as-is: a role has no email, so it's fed to FieldEditor as a
// pseudo-recipient with `label` standing in for `name` and an empty email
// (FieldEditor only reads `localId`/`role`/`name`/`email` for its palette
// buttons and accent colors — it never validates email format itself).
import * as React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Loader2, Plus, Trash2 } from 'lucide-react';
import { PageHeader } from '../../../design-system/components/PageHeader';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { LoadingState } from '../../../design-system/components/LoadingState';
import { ErrorState } from '../../../design-system/components/ErrorState';
import { NoAccessState } from '../../../design-system/components/NoAccessState';
import { useConfirm } from '../../../components/ui/confirm-dialog';
import { usePermission } from '../../../core/auth/usePermission';
import { esignTemplatesApi } from '../api';
import { loadPdf, type PDFDocumentProxy } from '../pdf';
import { makeLocalId, type DraftRecipient } from '../components/RecipientsEditor';
import { FieldEditor, type DraftField } from '../components/FieldEditor';
import { UseTemplateDialog } from '../components/UseTemplateDialog';
import type { EsignRecipientRole, EsignSigningOrder, EsignTemplate } from '../types';

interface DraftRole {
  localId: string;
  id: string | null;
  label: string;
  role: EsignRecipientRole;
}

function rolesAreValid(roles: DraftRole[]): boolean {
  return (
    roles.length > 0 &&
    roles.some((r) => r.role === 'signer') &&
    roles.every((r) => r.label.trim().length > 0)
  );
}

export function TemplateEditorPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const canView = usePermission('esign.view');
  const canSend = usePermission('esign.send');

  const [template, setTemplate] = React.useState<EsignTemplate | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [loadError, setLoadError] = React.useState<string | null>(null);

  const [name, setName] = React.useState('');
  const [signingOrder, setSigningOrder] = React.useState<EsignSigningOrder>('parallel');
  const [roles, setRoles] = React.useState<DraftRole[]>([]);
  const [fields, setFields] = React.useState<DraftField[]>([]);

  const [pdf, setPdf] = React.useState<PDFDocumentProxy | null>(null);
  const [pdfError, setPdfError] = React.useState<string | null>(null);

  const [saving, setSaving] = React.useState(false);
  const [archiving, setArchiving] = React.useState(false);
  const [useDialogOpen, setUseDialogOpen] = React.useState(false);

  const load = React.useCallback(() => {
    if (!id || !canView) return;
    setLoading(true);
    setLoadError(null);
    esignTemplatesApi.getTemplate(id)
      .then((detail) => {
        setTemplate(detail.template);
        setName(detail.template.name);
        setSigningOrder(detail.template.signing_order);
        const roleLocalIdByServerId = new Map<string, string>();
        const draftRoles: DraftRole[] = detail.roles.map((r) => {
          const localId = makeLocalId();
          roleLocalIdByServerId.set(r.id, localId);
          return { localId, id: r.id, label: r.label, role: r.role };
        });
        setRoles(draftRoles);
        setFields(
          detail.fields.map((f) => ({
            localId: makeLocalId(),
            recipientLocalId: roleLocalIdByServerId.get(f.template_role_id) ?? f.template_role_id,
            page: f.page,
            x: f.x,
            y: f.y,
            w: f.w,
            h: f.h,
            type: f.type,
            required: f.required,
          })),
        );
      })
      .catch((err: any) => setLoadError(err?.message ?? 'Failed to load this template'))
      .finally(() => setLoading(false));
  }, [id, canView]);

  React.useEffect(() => { load(); }, [load]);

  // Load the PDF bytes for the field editor once the template's loaded.
  React.useEffect(() => {
    if (!id || pdf || pdfError || loading) return;
    let cancelled = false;
    esignTemplatesApi.downloadPdf(id)
      .then((objectUrl) => fetch(objectUrl).then((r) => r.arrayBuffer()))
      .then((buf) => loadPdf(buf))
      .then((doc) => { if (!cancelled) setPdf(doc); })
      .catch((err: any) => { if (!cancelled) setPdfError(err?.message ?? 'Failed to load the PDF'); });
    return () => { cancelled = true; };
  }, [id, pdf, pdfError, loading]);

  const pseudoRecipients: DraftRecipient[] = React.useMemo(
    () => roles.map((r) => ({ localId: r.localId, id: r.id, role: r.role, name: r.label, email: '', employee_id: null })),
    [roles],
  );

  function addRole() {
    setRoles((prev) => [...prev, { localId: makeLocalId(), id: null, label: '', role: 'signer' }]);
  }

  function updateRole(localId: string, patch: Partial<DraftRole>) {
    setRoles((prev) => prev.map((r) => (r.localId === localId ? { ...r, ...patch } : r)));
  }

  async function removeRole(localId: string) {
    const affected = fields.filter((f) => f.recipientLocalId === localId);
    if (affected.length > 0) {
      const ok = await confirm({
        title: 'Remove role?',
        description: `This role has ${affected.length} field${affected.length === 1 ? '' : 's'} placed. Removing it will also remove those fields.`,
        confirmLabel: 'Remove',
        destructive: true,
      });
      if (!ok) return;
      setFields((prev) => prev.filter((f) => f.recipientLocalId !== localId));
    }
    setRoles((prev) => prev.filter((r) => r.localId !== localId));
  }

  async function handleSave() {
    if (!id) return;
    if (!name.trim()) { toast.error('Give this template a name.'); return; }
    if (!rolesAreValid(roles)) { toast.error('Add at least one signer role with a label.'); return; }
    setSaving(true);
    try {
      await esignTemplatesApi.updateTemplate(id, {
        name: name.trim(),
        signingOrder,
        roles: roles.map((r, i) => ({ localId: r.localId, label: r.label.trim(), role: r.role, order_index: i })),
        fields: fields.map((f) => ({
          roleLocalId: f.recipientLocalId,
          page: f.page, x: f.x, y: f.y, w: f.w, h: f.h, type: f.type, required: f.required,
        })),
      });
      toast.success('Template saved');
      load();
    } catch (err: any) {
      toast.error('Failed to save template', { description: err?.message ?? String(err) });
    } finally {
      setSaving(false);
    }
  }

  async function handleArchive() {
    if (!id) return;
    const ok = await confirm({
      title: 'Archive this template?',
      description: 'It will no longer be usable to create new documents. This cannot be undone.',
      confirmLabel: 'Archive',
      destructive: true,
    });
    if (!ok) return;
    setArchiving(true);
    try {
      await esignTemplatesApi.archiveTemplate(id);
      toast.success('Template archived');
      navigate('/dashboard/esign/templates');
    } catch (err: any) {
      toast.error('Failed to archive template', { description: err?.message ?? String(err) });
    } finally {
      setArchiving(false);
    }
  }

  if (!canView) {
    return (
      <div className="min-h-screen">
        <PageHeader eyebrow="Money · E-Sign" title="Template" />
        <div className="px-6 sm:px-8 lg:px-10 pb-12">
          <NoAccessState description="Your role doesn't include e-sign. Ask a workspace owner if you think you should have access." />
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen">
        <PageHeader eyebrow="Money · E-Sign" title="Template" />
        <div className="px-6 sm:px-8 lg:px-10 pb-12"><LoadingState /></div>
      </div>
    );
  }

  if (loadError || !template) {
    return (
      <div className="min-h-screen">
        <PageHeader eyebrow="Money · E-Sign" title="Template" />
        <div className="px-6 sm:px-8 lg:px-10 pb-12">
          <ErrorState title="Couldn't load this template" description={loadError ?? undefined} onRetry={load} />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <PageHeader
        eyebrow="Money · E-Sign"
        title={template.name}
        description="Edit roles and field placements, or use this template to create a document."
        actions={
          canSend ? (
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={() => setUseDialogOpen(true)}>Use template</Button>
              <Button variant="destructive" onClick={handleArchive} disabled={archiving || !template.is_active}>
                {archiving ? 'Archiving…' : template.is_active ? 'Archive' : 'Archived'}
              </Button>
            </div>
          ) : undefined
        }
      />

      <div className="px-6 sm:px-8 lg:px-10 pb-16 space-y-8">
        <div className="max-w-lg space-y-4 rounded-2xl bg-white/70 dark:bg-white/[0.04] ring-1 ring-ink/8 dark:ring-white/10 p-6">
          <label className="block">
            <span className="text-[13px] font-semibold text-ink/70 dark:text-white/70">Template name</span>
            <Input className="mt-1.5" value={name} onChange={(e) => setName(e.target.value)} disabled={!canSend} />
          </label>

          <div className="flex items-center gap-6">
            <span className="text-[13px] font-semibold text-ink/70 dark:text-white/70">Signing order</span>
            <label className="flex items-center gap-2 text-[13.5px] cursor-pointer">
              <input
                type="radio"
                name="template-signing-order"
                checked={signingOrder === 'parallel'}
                disabled={!canSend}
                onChange={() => setSigningOrder('parallel')}
              />
              Anyone, any order
            </label>
            <label className="flex items-center gap-2 text-[13.5px] cursor-pointer">
              <input
                type="radio"
                name="template-signing-order"
                checked={signingOrder === 'sequential'}
                disabled={!canSend}
                onChange={() => setSigningOrder('sequential')}
              />
              In this order
            </label>
          </div>
        </div>

        <section className="space-y-3">
          <h2 className="text-[13px] font-semibold uppercase tracking-[0.1em] text-ink/55 dark:text-white/55">
            Roles
          </h2>
          {roles.map((r) => (
            <div
              key={r.localId}
              className="flex items-center gap-2 rounded-2xl bg-white/70 dark:bg-white/[0.04] ring-1 ring-ink/8 dark:ring-white/10 p-3"
            >
              <Input
                placeholder="Role label, e.g. Employee"
                aria-label="Role label"
                value={r.label}
                onChange={(e) => updateRole(r.localId, { label: e.target.value })}
                className="flex-1"
                disabled={!canSend}
              />
              <select
                value={r.role}
                aria-label="Role kind"
                className="flex h-11 items-center rounded-2xl bg-white/80 dark:bg-white/[0.04] px-4 py-2 text-[13.5px] ring-1 ring-ink/8 dark:ring-white/10 w-[180px] disabled:opacity-50"
                disabled={!canSend}
                onChange={(e) => updateRole(r.localId, { role: e.target.value as EsignRecipientRole })}
              >
                <option value="signer">Signer</option>
                <option value="cc">CC — view only</option>
              </select>
              {canSend && (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  aria-label={`Remove ${r.label || 'role'}`}
                  onClick={() => removeRole(r.localId)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}
            </div>
          ))}
          {canSend && (
            <Button type="button" variant="outline" size="sm" className="gap-1.5" onClick={addRole}>
              <Plus className="h-3.5 w-3.5" />
              Add role
            </Button>
          )}
        </section>

        <section className="space-y-3">
          <h2 className="text-[13px] font-semibold uppercase tracking-[0.1em] text-ink/55 dark:text-white/55">
            Field placements
          </h2>
          {pdfError && <p className="text-[13px] text-rose-600 dark:text-rose-400">{pdfError}</p>}
          {!pdf && !pdfError && (
            <div className="flex items-center gap-2 text-[13px] text-ink/50 dark:text-white/50">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading document…
            </div>
          )}
          {pdf && (
            <FieldEditor
              pdf={pdf}
              pageCount={template.page_count}
              recipients={pseudoRecipients}
              fields={fields}
              onFieldsChange={setFields}
            />
          )}
        </section>

        {canSend && (
          <div className="flex justify-end">
            <Button disabled={saving} onClick={handleSave}>
              {saving ? 'Saving…' : 'Save template'}
            </Button>
          </div>
        )}
      </div>

      <UseTemplateDialog open={useDialogOpen} onOpenChange={setUseDialogOpen} templateId={id} />
    </div>
  );
}
